<template>
  <div v-loading="loading">
    <Maintenance class="p-col-12 p-md-12" :info="businessInfo" :serveTip="serveTip">
      <template v-if="!bookingStatus">
        <!-- 我的信息 -->
        <MyInfo :value="{ ...form, cancelable }" @onLogout="logout"></MyInfo>
        <!-- 禁止預約信息 -->
        <MyDisabledInfo v-if="hitConfig && hitConfig.length" :value="hitConfig"></MyDisabledInfo>
        <!-- 我的預約申請 -->
        <MyBooking
          :serveTip="serveTip"
          :showAllBooking="showAllBooking"
          :bookingDetailsList="tabBookingList"
          @onAddBooking="getBookingNum"
          @doCancelBooking="(formData = $event), (CancelBookingVisible = true)"
        >
          <template slot="class">
            <el-radio-group v-model="status" style="margin-top: 16px;">
              <el-radio-button v-for="item in statusTabs" :key="item.value" :label="item.value">{{item.label}}</el-radio-button>
            </el-radio-group>
          </template>
        </MyBooking>
      </template>

      <!-- 預約結果 -->
      <BookingResults
        v-else
        :status="bookingStatus"
        :error="error"
        :errorCode="errorCode"
        @returnBooking="init"
      >
      </BookingResults>
    </Maintenance>
    <!-- 新增預約 -->
    <AddBookingDialog
      :visible="AddBookingVisible"
      :value="timeItems"
      @close="AddBookingVisible = false"
      @onAddBooking="onAddBooking"
    ></AddBookingDialog>
    <!-- 取消預約彈框 -->
    <CancelBookingDialog
      :visible="CancelBookingVisible"
      :value="formData"
      @close="CancelBookingVisible = false"
      @onCancelBooking="onCancelBooking"
    >
    </CancelBookingDialog>
  </div>
</template>

<script>
import * as BookingApplicationAPI from "@/api/Booking/BookingApplicationAPI";
import AddBookingDialog from "./components/AddBookingDialog.vue";
import CancelBookingDialog from "./components/CancelBookingDialog.vue";
import BookingResults from "./components/BookingResults.vue";
import Maintenance from "./components/Maintenance.vue";
import MyInfo from "./components/MyInfo.vue";
import MyBooking from "./components/MyBooking.vue";
import MyDisabledInfo from "./components/MyDisabledInfo.vue";
import ServiceTypeConfigAPI from "@/api/ServiceTypeConfigAPI";
import { generateId } from "@/util/key";
import { getWeek, checkServerStatus } from "@/util/util";
import { wordBreak } from "@/filters";

export default {
  name: "BookingInfo",
  components: {
    AddBookingDialog,
    CancelBookingDialog,
    BookingResults,
    Maintenance,
    MyInfo,
    MyBooking,
    MyDisabledInfo,
  },
  data() {
    return {
      loading: false,
      AddBookingVisible: false,
      CancelBookingVisible: false,
      bookingStatus: "",
      formData: {},
      showAllBooking: false,
      businessInfo: {}, // 獲取 業務維護信息 / 業務信息
      bookingList: [], // 預約
      bookingDetailsList: [], // 預約詳情
      bookingNum: [], // 獲取可預約數-<日期,<時段,可預約數>>
      timeItems: [], // 可預約的日期 數組 開始日期
      error: {}, // 預約失敗 原因
      errorCode: "", // 預約失敗 原因
      cancelInfo: {}, // 取消次數信息
      hitConfig: [], // 禁止預約信息
      serveTip: "",

      status: "CONFIRMED", // 列表篩選預約狀態
      statusTabs: [
        {
          label: this.$t("message.Booking.success"),
          value: "CONFIRMED",
        },
        {
          label: this.$t("message.Booking.EXPIRED90"),
          value: "EXPIRED",
        },
        {
          label: this.$t("message.Booking.CANCELED90"),
          value: "CANCELED",
        },
      ]
    };
  },
  computed: {
    form: {
      get() {
        return this.$store.state.verifyForm;
      },
      set(value) {
        this.$store.commit("changeVerifyForm", value);
      },
    },
    serviceCode() {
      return "HZMB_H_BOOKING";
    },
    // 本月可取消次數
    cancelable() {
      const { allowCancelNum, cancelTotalNum } = this.cancelInfo || {};
      return `${allowCancelNum || "0"}/${cancelTotalNum || "0"}`;
    },
    // 驗證碼 999999 接口定義萬能驗證碼，新增預約和取消預約用，獲取預約則用登錄時候的
    captcha() {
      return "999999";
    },
    tabBookingList() {
      return this.bookingDetailsList?.filter(item => item.status === this.status) || [];
    },
  },
  methods: {
    getWeek,
    // 退出登錄
    async logout() {
      try {
        this.loading = true;
        await BookingApplicationAPI.logout(this.form.token);
      } catch (error) {
        this.apiError(error);
      } finally {
        this.loading = false;
        localStorage.setItem("form", "");
        localStorage.setItem("verifyForm", "");
        this.$store.commit("changeVerifyForm", {});
        this.$router.push("/");
      }
    },
    // 初始化
    init() {
      this.CancelBookingVisible = false;
      this.AddBookingVisible = false;
      this.showAllBooking = false;
      this.formData = {};
      this.bookingStatus = null;
      this.getBooking();
    },
    // 獲取日期時間
    getDateTime(data, showWeek) {
      const { applicationDate, fromTime, toTime } = data;
      const weekDate = showWeek ? `(${this.getWeek(applicationDate)}) ` : "";
      return `${this.$formatDate(
        applicationDate,
        "dd/MM/yyyy"
      )} ${weekDate}${fromTime.substring(0, 5)}-${toTime.substring(0, 5)}`;
    },
    getStatus(item) {
      // 新增申请=已确认，赴约状态=无
      // 取消申请=已取消
      // 其他状态用不上
      // 已过期是已确认，当前时间已过
      const { details = [] } = item;
      if (['USER_CANCELED', 'CANCELED'].includes(details[0].status)) return "CANCELED"; // 已取消
      else {
        const _date = new Date(this.$formatDate(new Date(), "yyyy-MM-dd")).getTime();
        const applicationDate = new Date(details[0].applicationDate).getTime();
        if (_date > applicationDate) return "EXPIRED"; // 已過期
        return "CONFIRMED"; // 生效中
      }
    },
    // 獲取 獲取業務信息 / 業務維護信息
    async getBusinessInfo() {
      const business = await ServiceTypeConfigAPI.getDetails(this.serviceCode);
      if (!business) {
        throw this.$t("message.ErrorMessage.BUSINESS_NOT_FOUND");
      }
      // 設置頁面標題
      this.$store.commit("changeServiceTypeConfig", {
        nameEn: wordBreak(business.nameEn),
        namePt: wordBreak(business.namePt),
        nameSc: wordBreak(business.nameSc),
        nameTc: wordBreak(business.nameTc),
      });
      return business;
    },
    // 新增預約
    async onAddBooking(item) {
      try {
        this.loading = true;
        this.AddBookingVisible = false;
        const { mobile, applicantIdentNo, applicantIdentType, name } = this.form;
        const value = {
          businessCode: this.serviceCode,
          contactss: [],
          details: [
            {
              ...item,
              applicationDate: item.date,
              fromTime: item.time.split("-")[0] || "",
              toTime: item.time.split("-")[1] || "",
            },
          ],
          language: this.$t("message.lang"),
          mainEmail: null,
          mainName: name,
          mainPhone: mobile,
          applicantIdentNo,
          applicantIdentType,
        };
        await BookingApplicationAPI.submit(value, mobile, this.form.token);
        await this.getBooking();
      } catch (error) {
        this.AddBookingVisible = false;
        this.apiError(error);
      } finally {
        this.loading = false;
      }
    },
    // 取消預約
    async onCancelBooking() {
      try {
        this.loading = true;
        this.CancelBookingVisible = false;
        await BookingApplicationAPI.cancel(this.formData.id, this.form.token, this.$t("message.lang"));
        // 本月可取消次數（剩餘/最多）
        this.cancelInfo = await BookingApplicationAPI.getCancelInfo({
          carNo: this.form.name,
          businessCode: this.serviceCode,
        }, this.form.token);
        this.bookingStatus = "cancel";
      } catch (error) {
        this.apiError(error);
      } finally {
        this.loading = false;
      }
    },
    // 獲取當前登錄賬號的預約 / 獲取業務維護信息
    async getBooking() {
      try {
        this.loading = true;
        let bookingList =
          (await BookingApplicationAPI.getList({
            businessCode: this.serviceCode,
            phone: this.form.mobile,
            carNo: this.form.name,
            applicantIdentNo: this.form.applicantIdentNo,
            applicantIdentType: this.form.applicantIdentType,
          }, this.form.token)) || [];
        // 修改時間排序
        bookingList.sort((a, b) => {
          let x = b.details?.[0]?.applicationDate;
          let y = a.details?.[0]?.applicationDate;
          return x < y ? 1 : x > y ? -1 : 0;
        });
        this.bookingList = bookingList || [];
        this.bookingDetailsList = [];

        for (var i = 0; i < bookingList.length; i++) {
          const details = bookingList[i].details;
          if (details && details.length) {
            const parameterValue = JSON.parse(bookingList[i].parameterValue || "{}");
            this.bookingDetailsList.push({
              ...bookingList[i],
              parameterValue,
              details: details,
              status: this.getStatus(bookingList[i]),
            });
          }
        }
      } catch (error) {
        if (error !== "cancel" && error !== "close" && error !== false)
          this.$error(error);
      } finally {
        this.loading = false;
      }
    },
    // 新增預約 - 選擇
    // 獲取可預約數-<日期,<時段,可預約數>>
    async getBookingNum() {
      try {
        if (!this.serviceCode) return;
        this.loading = true;
        this.bookingNum =
          (await BookingApplicationAPI.getBookingNum({
            businessCode: this.serviceCode,
            carNo: this.form.name,
          }, this.form.token)) || {};
        const newTimeItems = [];
        console.log("this.bookingNum", this.bookingNum);

        for (let date in this.bookingNum) {
          const { durations, limitType, receptions } = this.bookingNum[date];
          for (let time in durations) {
            let num = durations[time] > 0 ? durations[time] : 0;

            // 不限制人數 判断
            if (limitType === "NOT_LIMIT") num = 999;
            // 限制天数 判断
            else if (limitType === "DAY") num = receptions || 0;

            const key = generateId();
            newTimeItems.push({
              key,
              limitType,
              date: date,
              time: time,
              num: num,
              label: `${this.$formatDate(date, "dd/MM/yyyy")} ${time}`,
            });
          }
        }
        this.timeItems = newTimeItems.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        this.AddBookingVisible = true;
      } catch (error) {
        if (error !== "cancel" && error !== "close" && error !== false)
          this.$error(error);
      } finally {
        this.loading = false;
      }
    },
    // api 報錯信息處理
    apiError(error) {
      if (error !== "cancel" && error !== "close" && error !== false) {
        if (error.code == "OVER_BUSINESS_APPLACATION_TIMES") {
          let setNumber = error.message && error.message.split("：");
          setNumber = (setNumber.length && setNumber[setNumber.length - 1]) || "";
          this.errorCode = this.$t(
            "message.ErrorMessage.OVER_BUSINESS_APPLACATION_TIMES",
            [setNumber]
          );
        } else {
          this.errorCode = error.code
            ? this.$t(`message.ErrorMessage.${error.code}`)
            : "";
        }
        this.error = error || {};
        this.bookingStatus = "error";
      }
    },
    // 處理自定義屬性 轉 json
    switchToParameterValue(object, keys = [], target = "parameterValue") {
      if (!object) return object;
      const obj = Object.assign({}, object);
      const parameterValue = {};
      for (const key of keys) {
        if (obj[key] !== undefined) {
          parameterValue[key] = obj[key];
          delete obj[key];
        }
      }
      obj[target] = JSON.stringify(parameterValue);
      return obj;
    },
  },
  async created() {
    try {
      this.loading = true;
      const businessInfo = await this.getBusinessInfo();
      this.businessInfo = businessInfo || {};
      await checkServerStatus(this.serviceCode);
    } catch (error) {
      if(typeof error === "string") {
        this.serveTip = error
      }
      this.$error(error);
    } finally {
      this.loading = false;
    }

    if (!this.form || !this.form.mobile || !this.form.token || !this.form.name || !this.form.vin || !this.form.applicantIdentNo || !this.form.applicantIdentType) {
      this.$router.push('/hzmb-truck-mo-booking');
    } else {
      await this.getBooking();
      this.loading = true;
      // 獲取基礎數據
      Promise.all([
        BookingApplicationAPI.getCancelInfo({
          carNo: this.form.name,
          businessCode: this.serviceCode,
        }, this.form.token), // 獲取 本月可取消次數（剩餘/最多）
        BookingApplicationAPI.getHitConfig({ carNo: this.form.name }, this.form?.token), // 獲取 禁止預約信息
      ])
        .then((res) => {
          const [cancelInfo = {}, hitConfig = []] = res;
          this.cancelInfo = cancelInfo;
          this.hitConfig = hitConfig;
        })
        .catch((error) => {
          this.$error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
};
</script>

<style lang="scss" scoped>
/deep/.formGroup_content {
  max-width: 100%;
}
/deep/.Container {
  max-width: 902px;
  margin: auto;
  padding: 24px 14px;
  font-size: 20px;
  .card-header {
    font-size: 26px;
    font-weight: bold;
    text-align: center;
  }
  .card-content {
    text-align: center;
  }
}
.el-button--primary.is-plain:focus,
.el-button--primary.is-plain:hover {
  background: #5ba7ad;
  border-color: #5ba7ad;
  color: #fff;
}
.el-button--primary.is-plain {
  color: #39959e;
  background: #fff;
}
::v-deep .el-button{
  font-size: 16px;
}

</style>
